import { cn } from "@/lib/cn";
import { useLocation, useRouter, useSearch } from "@tanstack/react-router";
import { memo, useMemo } from "react";
import HStack from "../../Layout/HStack";
import { ScrollButton } from "./ScrollButton";
import { Tab } from "./Tab";
import { applyActiveState } from "./tabState";
import type { HeaderTabsProps, TabWithIndicatorProps } from "./types";
import { useScrollableNavigation } from "./useScrollableNavigation";

// 型を明示的にエクスポート
export type { TabWithIndicatorProps };

const TabList = memo(
  ({
    tabs,
    ariaLabel,
  }: {
    tabs: HeaderTabsProps["tabs"];
    ariaLabel?: string;
  }) => (
    <HStack className="gap-4 min-w-max" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab, i) => (
        <Tab key={i} {...tab} />
      ))}
    </HStack>
  ),
);

/**
 * スクロール可能なタブナビゲーションを表示するコンポーネント
 *
 * TanStack Routerの標準的なactiveOptions機能を利用しています:
 * - exact: true - URLパスの完全一致判定（子ルートを含まない）
 * - includeSearch: true/false - クエリパラメータの有無を考慮するかどうか
 * - includeHash: true/false - ハッシュフラグメントを考慮するかどうか
 *
 * @see {@link https://tanstack.com/router/latest/docs/framework/react/guide/navigation#active-options}
 */
export const HeaderTabs = memo(function HeaderTabs({
  tabs,
  "aria-label": ariaLabel,
  className,
}: HeaderTabsProps) {
  const {
    containerRef,
    handleScroll,
    scrollToPrevious,
    scrollToNext,
    isAtStart,
    isAtEnd,
  } = useScrollableNavigation();

  const location = useLocation();
  const router = useRouter();
  const searchResult = useSearch({ strict: false });
  const currentSearch =
    typeof searchResult === "object" && searchResult !== null
      ? (searchResult as Record<string, unknown>)
      : undefined;

  const tabsWithActive = useMemo(
    () =>
      applyActiveState(
        tabs,
        {
          pathname: location.pathname,
          search: location.searchStr,
          hash: location.hash,
        },
        router,
        currentSearch,
      ),
    [
      tabs,
      location.pathname,
      location.searchStr,
      location.hash,
      router,
      currentSearch,
    ],
  );

  return (
    <div className={cn("flex relative", className)}>
      <div
        ref={containerRef}
        className="flex overflow-hidden w-full"
        onScroll={handleScroll}
      >
        <TabList tabs={tabsWithActive} ariaLabel={ariaLabel} />
      </div>

      <ScrollButton
        direction="left"
        onClick={scrollToPrevious}
        disabled={isAtStart}
      />
      <ScrollButton
        direction="right"
        onClick={scrollToNext}
        disabled={isAtEnd}
      />
    </div>
  );
});
